/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IActiveImageAdditionalContentViewProps, IArea } from '@msdyn365-commerce-modules/active-image/src/modules/active-image/../active-image-utilities';
import { IActiveImageViewProps } from '@msdyn365-commerce-modules/active-image/src/modules/active-image/./active-image';

import { getProductPageUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import { ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import { IActiveImageConfig } from '@msdyn365-commerce-modules/active-image/src/modules/active-image';

/**
 * Render Additional Content.
 * @param additionalContent - Additional content view props.
 * @returns JSX Element.
 */
const renderAdditionalContent = (additionalContent: IActiveImageAdditionalContentViewProps) => {
    return (
        <Node {...additionalContent.additionalContentNode}>
            {additionalContent.heading}
            <Node {...additionalContent.additionalContentContainer}>
                {additionalContent.text}
                <Node {...additionalContent.additionalContentItemLinks}>
                    {additionalContent.links}
                </Node>
            </Node>
        </Node >
    );
};

/**
 * Render View.
 * @param props - The view props.
 * @returns -The JSX Element.
 */
export const activeImageView: React.FC<IActiveImageViewProps> = props => {
    const hoverArea = (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.TouchEvent<HTMLButtonElement>, index: number) => {
        const target = event.target as HTMLElement;
        const map = target?.closest('.msc-active-image__area-with-dots')?.getElementsByClassName('msc-active-image-map')[0] as HTMLMapElement;
        map?.areas[index].dispatchEvent(new MouseEvent('mouseover', { 'bubbles': true }));
        const mapCtn = target?.closest('.msc-active-image')?.getElementsByClassName('msc-active-image-product-card-container')[0];
        const firstElement = mapCtn && mapCtn.firstChild as HTMLElement;
        if(firstElement){
            firstElement.style.display = 'block';
        }
    }
    const leaveArea = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) => {
        const target = event.target as HTMLElement;
        const map = target?.closest('.msc-active-image__area-with-dots')?.getElementsByClassName('msc-active-image-map')[0] as HTMLMapElement;
        map?.areas[index].dispatchEvent(new MouseEvent('mouseleave', { 'bubbles': true }));
        const mapCtn = target?.closest('.msc-active-image')?.getElementsByClassName('msc-active-image-product-card-container')[0];
        const firstElement = mapCtn && mapCtn.firstChild as HTMLElement;
        if(firstElement){
            firstElement.style.display = 'none';
        }
    }
    const clickArea = (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.TouchEvent<HTMLButtonElement>, index: number) => {
        const target = event.target as HTMLElement;
        const map = target?.closest('.msc-active-image__area-with-dots')?.getElementsByClassName('msc-active-image-map')[0] as HTMLMapElement;
        map?.areas[index].dispatchEvent(new MouseEvent('click', { 'bubbles': true }));
    }

    // @ts-ignore
    const products = props.data.products.result;
    console.log(props.data)
    const existingPoints:string[] = [];
    const existingPointIds:string[] = [];
    const _activePoints: IArea[] = [];
    const _activeImageClass = `msc-active-image-${getActiveImageId(props.config)}`;
    const addActivePoints = (area: IArea | undefined, product: ProductSearchResult, coords: number[], productUrl: string): void => {
        if (!area) {
            _activePoints.push({
                areaId: product.RecordId.toString(),
                coords,
                primaryImageUrl: product.PrimaryImageUrl ?? '',
                productUrl,
                productSearchResult: product
            });
        }
    };
    products && products.map((product: ProductSearchResult) => {
        existingPoints.push(product.RecordId.toString());
        product.ItemId && existingPointIds.push(product.ItemId);
        console.log(existingPoints);
        const defaultPosition: number = 0;
        if(props.config.activePoints) {
            for (const configProduct of props.config.activePoints) {
                if (isNaN(Number(configProduct.itemId)) ? product.ItemId === configProduct.itemId : product.RecordId === Number(configProduct.itemId)) {
                    const productUrl = getProductPageUrlSync(product.Name ?? '', product.RecordId, props.context.actionContext, undefined);
                    const coords = [
                        configProduct.xPosition ?? defaultPosition,
                        configProduct.yPosition ?? defaultPosition,
                        configProduct.radius ?? defaultPosition
                    ];
                    const area = _activePoints.find(activePoint => JSON.stringify(activePoint.coords) === JSON.stringify(coords));
                    addActivePoints(area, product, coords, productUrl);
                }
            }
        }
    });

    const imageMapperExt = React.cloneElement(props.imageMapper as React.ReactElement, {areaMap: {name: `map-${_activeImageClass}`, areas: _activePoints}});

    return (
        <Module {...props.activePointContainer}>
            <Node {...props.activePointDetailsContainer}>
                <div className={'msc-active-image__area-with-dots'}>
                    {imageMapperExt}
                    { props.config.activePoints && props.config.activePoints.map((activePoint, index) => {
                        if(activePoint.itemId && (existingPoints.includes(activePoint.itemId) || existingPointIds.includes(activePoint.itemId)) && activePoint.xPosition && activePoint.yPosition && props.config.activeImage && props.config.activeImage.width && props.config.activeImage.height){
                            const leftP = activePoint.xPosition * 100 / props.config.activeImage.width;
                            const topP = activePoint.yPosition * 100 / props.config.activeImage.height;
                        
                            return(
                                <button className={'msc-active-image__area-dot'} style={{ left: `${leftP}%`, top: `${topP}%` }}
                                        onMouseEnter={(event) => hoverArea(event,index)}
                                        onClick={(event) => clickArea(event, index)}
                                        onTouchEnd={(event) => hoverArea(event,index)}
                                        onMouseLeave={(event) => leaveArea(event,index)}
                                >{props.resources.seeMoreButtonText}</button>
                            )
                        }
                        return;
                    })
                    }
                </div>
                <Node {...props.detailsContainer} className={props.detailsContainer ? props.detailsContainer.className : ''}>
                    {props.title}
                    {props.text}
                    {props.additionalContent && renderAdditionalContent(props.additionalContent)}
                    <Node {...props.detailsContainerCta} className={props.detailsContainerCta ? props.detailsContainerCta.className : ''}>
                        {props.links}
                    </Node>
                </Node>
            </Node>

            <Node
                className='msc-active-image-product-card-container'
                style={{ top: `${props.productCardtopPosition}px`, left: `${props.productCardleftPosition}px` }}>
                {props.overlay}
                {props.productCard}
            </Node>
        </Module>
    );
};

const getActiveImageId = (config: IActiveImageConfig) => {
    const { activeImage } = config;
    let activeImageId: string = '';
    if (activeImage?.src) {
        const activeImageSource = activeImage.src.split('/');
        const decrementOne: number = 1;
        const activeImageIdPart = activeImageSource[activeImageSource.length - decrementOne].split('?');
        activeImageId = activeImageIdPart[0] ?? '';
    }
    return activeImageId;
};

export default activeImageView;
